import {
	WidgetInputComplexNumericField,
	WidgetLabel,
	WidgetOptInCheckbox,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import type { TextAlignMode, TextAlignValue, TextShadowValue, TextStyle } from '../../data/v1'
import { FontFamilyInspector } from './font-family'
import { TextAlignInspector } from './text-align'
import { TextColorInspector } from './text-color'

type FontStyleValue = TextStyle['fontStyleValue']

const fontStyleItems: WidgetSelectGroupItem<FontStyleValue>[] = [
	{ id: 'normal', label: 'Normal', render: () => <span className="text-xs">Normal</span> },
	{ id: 'italic', label: 'Italic', render: () => <span className="text-xs italic">Italic</span> },
]

const textShadowValueItems: WidgetSelectGroupItem<TextShadowValue>[] = [
	{ id: 'none', label: 'None', render: () => <span className="text-xs">None</span> },
	{ id: 'simple', label: 'Simple', render: () => <span className="text-xs">Simple</span> },
]

export type TextFormattingInspectorProps = {
	textAlignMode: TextAlignMode
	textAlignValue: TextAlignValue
	onTextAlignModeChange: (mode: TextAlignMode) => void
	onTextAlignValueChange: (value: TextAlignValue) => void
	textStyle: TextStyle
	onTextStyleChange: (changes: Partial<TextStyle>) => void
}

export function TextFormattingInspector({
	textAlignMode,
	textAlignValue,
	onTextAlignModeChange,
	onTextAlignValueChange,
	textStyle,
	onTextStyleChange,
}: TextFormattingInspectorProps) {
	return (
		<div className="flex flex-col gap-2">
			<TextAlignInspector
				mode={textAlignMode}
				value={textAlignValue}
				onModeChange={onTextAlignModeChange}
				onChange={onTextAlignValueChange}
			/>

			<TextColorInspector
				mode={textStyle.textColorMode}
				onModeChange={(textColorMode) => onTextStyleChange({ textColorMode })}
				value={textStyle.textColor}
				onChange={(textColor) => onTextStyleChange({ textColor })}
			/>

			<FontFamilyInspector
				mode={textStyle.fontFamilyMode}
				provider={textStyle.fontFamilyProvider}
				googleFamily={textStyle.fontFamilyGoogleFamily}
				customUrl={textStyle.fontFamilyCustomUrl}
				customName={textStyle.fontFamilyCustomName}
				onModeChange={(fontFamilyMode) => onTextStyleChange({ fontFamilyMode })}
				onProviderChange={(fontFamilyProvider) => onTextStyleChange({ fontFamilyProvider })}
				onGoogleFamilyChange={(fontFamilyGoogleFamily) => onTextStyleChange({ fontFamilyGoogleFamily })}
				onCustomFontChange={(fontFamilyCustomUrl, fontFamilyCustomName) =>
					onTextStyleChange({ fontFamilyCustomUrl, fontFamilyCustomName })}
			/>

			<WidgetLabel label="Font size">
				<WidgetOptInCheckbox
					optedIn={textStyle.fontSizeMode === 'value'}
					onOptIn={() => onTextStyleChange({ fontSizeMode: 'value' })}
					onOptOut={() => onTextStyleChange({ fontSizeMode: 'auto' })}
				>
					<WidgetInputComplexNumericField
						value={textStyle.fontSizePxValue}
						onChange={(fontSizePxValue) => onTextStyleChange({ fontSizePxValue })}
						min={1}
						defaultValue={16}
						suffix="px"
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Font weight">
				<WidgetOptInCheckbox
					optedIn={textStyle.fontWeightMode === 'value'}
					onOptIn={() => onTextStyleChange({ fontWeightMode: 'value' })}
					onOptOut={() => onTextStyleChange({ fontWeightMode: 'auto' })}
				>
					<WidgetInputComplexNumericField
						value={textStyle.fontWeightValue}
						onChange={(fontWeightValue) => onTextStyleChange({ fontWeightValue })}
						min={100}
						max={900}
						step={100}
						sensitivity={0.05}
						defaultValue={400}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Font style">
				<WidgetOptInCheckbox
					optedIn={textStyle.fontStyleMode !== 'auto'}
					onOptIn={() => onTextStyleChange({ fontStyleMode: 'value' })}
					onOptOut={() => onTextStyleChange({ fontStyleMode: 'auto' })}
				>
					<WidgetSelectGroup
						value={textStyle.fontStyleValue}
						onChange={(fontStyleValue) => onTextStyleChange({ fontStyleValue })}
						items={fontStyleItems}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Line height">
				<WidgetOptInCheckbox
					optedIn={textStyle.lineHeightMode === 'value'}
					onOptIn={() => onTextStyleChange({ lineHeightMode: 'value' })}
					onOptOut={() => onTextStyleChange({ lineHeightMode: 'auto' })}
				>
					<WidgetInputComplexNumericField
						value={textStyle.lineHeightValue}
						onChange={(lineHeightValue) => onTextStyleChange({ lineHeightValue })}
						min={0.5}
						max={5}
						step={0.01}
						defaultValue={1.4}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			<WidgetLabel label="Text shadow">
				<WidgetOptInCheckbox
					optedIn={textStyle.textShadowMode !== 'auto'}
					onOptIn={() => onTextStyleChange({ textShadowMode: 'value' })}
					onOptOut={() => onTextStyleChange({ textShadowMode: 'auto' })}
				>
					<WidgetSelectGroup
						value={textStyle.textShadowValue}
						onChange={(textShadowValue) => onTextStyleChange({ textShadowValue })}
						items={textShadowValueItems}
					/>
				</WidgetOptInCheckbox>
			</WidgetLabel>

			{textStyle.textShadowMode === 'value' && textStyle.textShadowValue === 'simple' && (
				<div className="grid grid-cols-2 gap-2">
					<WidgetLabel label="Shadow opacity">
						<WidgetInputComplexNumericField
							value={textStyle.textShadowSimple.opacity}
							onChange={(opacity) =>
								onTextStyleChange({
									textShadowSimple: { ...textStyle.textShadowSimple, opacity },
								})}
							min={0}
							max={100}
							step={1}
							defaultValue={20}
							suffix="%"
						/>
					</WidgetLabel>

					<WidgetLabel label="Shadow spread">
						<WidgetInputComplexNumericField
							value={textStyle.textShadowSimple.spread}
							onChange={(spread) =>
								onTextStyleChange({
									textShadowSimple: { ...textStyle.textShadowSimple, spread },
								})}
							min={0}
							max={20}
							step={1}
							defaultValue={5}
							suffix="px"
						/>
					</WidgetLabel>
				</div>
			)}
		</div>
	)
}
